import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import Cookies from 'js-cookie';
import { Volume2, VolumeX, Maximize2 } from 'lucide-react';

// Hooks
import SystemDataService from '../../Services/SystemDataService';
import useDisableScrolling from '../../Hooks/useDisableScrolling';
import useClickOutside from '../../Hooks/useClickOutside';
import useScrollOrNavigate from '../../Hooks/useScrollOrNavigate'; // YOUR HOOK

// UI Components
import { Modal } from './modal-component';
import { ButtonPrimary, ButtonSecondary } from '../Buttons/button-component';
import MediaComponent from '../Media/media-component';
import { isVideoUrl, toMediaUrl } from '../../Services/ApiService';

const COOKIE_NAME = 'promo_popup_dismissed_v1';

// Popup media: a portrait-friendly video (fits fully via object-contain on a
// dark backdrop) with unmute + fullscreen controls, or a plain image. Autoplay
// stays muted per browser policy; the viewer opts into sound.
const PopupMedia = ({ src, alt }) => {
    const videoRef = useRef(null);
    const [muted, setMuted] = useState(true);

    const isVideo = isVideoUrl(src);

    const toggleSound = useCallback((e) => {
        e.stopPropagation();
        const el = videoRef.current;
        if (!el) return;
        const next = !el.muted;
        el.muted = next;
        if (!next) el.play().catch(() => {});
        setMuted(next);
    }, []);

    const goFullscreen = useCallback((e) => {
        e.stopPropagation();
        const el = videoRef.current;
        if (!el) return;
        const req = el.requestFullscreen || el.webkitEnterFullscreen || el.webkitRequestFullscreen;
        if (req) req.call(el);
    }, []);

    if (!isVideo) {
        return (
            <MediaComponent
                src={src}
                alt={alt}
                width={800}
                quality={70}
                wrapperClassName="w-full h-full"
                autoPlayWidth={1}
            />
        );
    }

    return (
        <>
            {/* Inline: fill the card (object-cover). Fullscreen: preserve aspect
                ratio (object-contain) so nothing is cropped on the big screen. */}
            <style>{`
                .popup-video:fullscreen,
                .popup-video:-webkit-full-screen {
                    object-fit: contain !important;
                    background: #000;
                }
            `}</style>
            <video
                ref={videoRef}
                src={toMediaUrl(src)}
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
                className="popup-video w-full h-full object-cover"
            />

            {/* Controls */}
            <div className="absolute bottom-3 right-3 z-20 flex items-center gap-2">
                <button
                    type="button"
                    onClick={toggleSound}
                    aria-label={muted ? 'Unmute video' : 'Mute video'}
                    className="rounded-full bg-black/45 hover:bg-black/65 p-2.5 text-white backdrop-blur-sm transition-colors"
                >
                    {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                </button>
                <button
                    type="button"
                    onClick={goFullscreen}
                    aria-label="Play fullscreen"
                    className="rounded-full bg-black/45 hover:bg-black/65 p-2.5 text-white backdrop-blur-sm transition-colors"
                >
                    <Maximize2 className="w-5 h-5" />
                </button>
            </div>

            {/* Tap-to-unmute hint while muted */}
            {muted && (
                <button
                    type="button"
                    onClick={toggleSound}
                    className="absolute bottom-3 left-3 z-20 rounded-full bg-black/45 hover:bg-black/65 px-3 py-2 text-white text-xs font-secondary font-bold backdrop-blur-sm transition-colors"
                >
                    Tap for sound
                </button>
            )}
        </>
    );
};


const PopUpPromotion = ({ expand = true, delay = 4000 }) => {
    const systemData = useSelector((state) => state.systemData || {});
    const { language = 'he', dir = 'rtl' } = systemData;
    const phone = SystemDataService.byLabel(systemData?.links, 'Phone');

    const [config, setConfig] = useState(null);
    const [eligible, setEligible] = useState(false); 
    const [delayedOk, setDelayedOk] = useState(false);
    const [dismissed, setDismissed] = useState(false);
    
    const modalRef = useRef(null);
    const scrollOrNavigate = useScrollOrNavigate();

    const labels = {
        he: { call: 'התקשרו עכשיו', learnMore: 'למידע נוסף', defaultTitle: 'מבצע מיוחד!' },
        en: { call: 'Call Now', learnMore: 'Learn More', defaultTitle: 'Special Offer!' }
    };

    const t = labels[language] || labels.en;

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const data = await SystemDataService.getPopUpImage();
                // STRICT CHECK: If no data, no image, or status is false, do nothing.
                if (!mounted || !data || !data[0] || !data[0].image || data[0].status === false) {
                    return; 
                }
                
                setConfig(data[0]);
                const hasBeenDismissed = Cookies.get(COOKIE_NAME);
                setEligible(!hasBeenDismissed);
            } catch (e) {
                console.error('PopUp Config Error:', e);
            }
        })();
        return () => { mounted = false; };
    }, []);

    useEffect(() => {
        if (expand && eligible && !dismissed && config) {
            const timer = setTimeout(() => setDelayedOk(true), delay);
            return () => clearTimeout(timer);
        }
    }, [expand, eligible, dismissed, delay, config]);

    const handleDismiss = useCallback(() => {
        Cookies.set(COOKIE_NAME, 'true', { expires: 1 / 24 });
        setDismissed(true);
        setEligible(false);
    }, []);

    const handleActionClick = (e) => {
        handleDismiss();
        // A real target has no spaces (id / path / url). If actionButton holds a
        // label (legacy/cached entry), ignore it and scroll to the contact form.
        const raw = (config?.actionButton || '').trim();
        const target = raw && !/\s/.test(raw) ? raw : 'contact-form';
        if (target.startsWith('http')) {
            window.open(target, '_blank', 'noopener');
            return;
        }
        scrollOrNavigate(e, 'contact-form', target.startsWith('/') ? target : `/${target}`);
    };

    useClickOutside(modalRef, handleDismiss);

    // Visibility condition
    const isOpen = expand && eligible && delayedOk && !dismissed && config;
    useDisableScrolling(isOpen);

    // CRITICAL: If no config is loaded, or user isn't eligible, return null immediately (no fallback UI)
    if (!config || !eligible || !config.image) return null;

    // Button hierarchy: the top-most enabled CTA is primary, the other secondary.
    const showCall = config.isCall && phone?.url;
    const showAction = config.isActionButton;
    // Prefer actionLabel. Legacy/cached entries stored the button text in
    // actionButton (a value with spaces, not a target like 'contact-form'),     // fall back to that so the real label shows before the target default.
    const legacyLabel = /\s/.test((config.actionButton || '').trim()) ? config.actionButton.trim() : '';
    const actionLabel = config.actionLabel || legacyLabel || t.learnMore;
    const ActionButton = showCall ? ButtonSecondary : ButtonPrimary;

    return (
        <Modal expand={isOpen} timeout={false}>
            <div dir={dir} className="flex w-full h-full justify-center items-center p-2 font-primary">
                <div 
                    ref={modalRef}
                    className="relative w-full max-w-lg overflow-hidden bg-white dark:bg-neutral-900 rounded-[2.5rem] shadow-2xl animate-modal-in"
                >
                    <button
                        onClick={handleDismiss}
                        className={`absolute top-5 ${dir === 'rtl' ? 'left-5' : 'right-5'} z-20 rounded-full bg-black/5 hover:bg-black/10 p-2 transition-all hover:rotate-90 text-gray-800 dark:text-white dark:bg-white/10`}
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                    </button>

                    <div className="flex flex-col">
                        <div className="w-full h-[55vh] max-h-[620px] min-h-[20rem] relative bg-neutral-950 overflow-hidden">
                            <PopupMedia src={config.image} alt={config.title || 'Promotion'} />
                        </div>

                        <div className="p-8 md:py-8 px-10 flex flex-col gap-6 text-center">
                            <div className="space-y-3">
                                {config.badge && (
                                    <span className="mx-auto block w-fit rounded-full bg-secondary-50 px-4 py-1.5 font-secondary text-xs font-bold uppercase tracking-widest text-secondary-600">
                                        {config.badge}
                                    </span>
                                )}
                                <h3 className="font-secondary text-3xl md:text-5xl font-extrabold tracking-[-0.02em] text-primary-500 dark:text-white leading-[1.1]">
                                    {config.title || t.defaultTitle}
                                </h3>
                                {config.subtitle && (
                                    <p className="font-primary text-lg md:text-xl text-ink-muted dark:text-gray-400 leading-relaxed">
                                        {config.subtitle}
                                    </p>
                                )}
                            </div>

                            <div className="flex flex-col gap-3">
                                {showCall && (
                                    <ButtonPrimary
                                        size="lg"
                                        fullWidth
                                        href={phone.url}
                                        onClick={handleDismiss}
                                        className="!rounded-2xl shadow-xl shadow-primary-500/20 active:scale-95 transition-transform"
                                    >
                                        <span className="text-xl font-bold">{t.call}</span>
                                    </ButtonPrimary>
                                )}

                                {showAction && (
                                    <ActionButton
                                        size="lg"
                                        fullWidth
                                        onClick={handleActionClick}
                                        className="!rounded-2xl active:scale-95 transition-transform"
                                    >
                                        <span className="text-lg font-bold">{actionLabel}</span>
                                    </ActionButton>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default PopUpPromotion;